import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'

export class FetchError extends Error {
  status: number
  /** Field-keyed validation messages from a 422 (`details` in the v4 error body). */
  details?: Record<string, string[]>
  /** Machine code from a v4 refusal body — names the rule that said no. */
  code?: string
  constructor(status: number, message?: string, details?: Record<string, string[]>, code?: string) {
    super(message ?? `HTTP ${status}`)
    this.status = status
    this.details = details
    this.code = code
  }
}

interface FetchOptions {
  headers?: Record<string, string>
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
}

interface RequestOptions {
  silent?: boolean
}

type ErrorBody = {
  error?: string | string[]
  message?: string
  /** Field-keyed validation messages from a 422 (v4 error body). */
  details?: Record<string, string[]>
  /** Machine code from a v4 refusal body — names the rule that said no. */
  code?: string
}

export const useFetch = (url: string, _options?: FetchOptions) => {
  const notificationStore = useNotificationStore()
  const baseUrl = import.meta.env.VITE_VENTURE360_API_URL.replace('/v3', '')
  const authStore = useAuthStore()
  const baseOptions: RequestInit = {
    headers: {
      Authorization: `Bearer ${authStore.access_token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  }

  // Centralized error handling. 403 is always surfaced (the API returns it for
  // read-only "view as" sessions and for permission denials); other errors are
  // surfaced only when the caller did not opt into silence.
  const notifyOnError = async (res: Response, silent: boolean, fallback: string): Promise<never> => {
    if (res.status === 401) {
      throw new Error('Your session has expired. Please refresh and try again.')
    }

    let body: ErrorBody = {}
    let message = fallback
    try {
      const text = await res.text()
      body = text ? (JSON.parse(text) as ErrorBody) : {}
      message = body.message || (Array.isArray(body.error) ? body.error[0] : body.error) || fallback
    } catch {
      console.error(`${res.status} ${res.statusText}`)
    }

    if (res.status === 403) {
      const isReadOnly = Array.isArray(body.error) ? body.error.includes('read_only') : body.error === 'read_only'
      notificationStore.enqueue(
        'error',
        isReadOnly
          ? 'Changes are not allowed while viewing as another user. Log in as the user to make changes.'
          : message || 'You do not have permission to do that.',
      )
    } else if (!silent) {
      notificationStore.enqueue('error', message)
    }

    throw new FetchError(res.status, message, body.details, body.code)
  }

  const get = async <T>(opts?: RequestOptions): Promise<T> => {
    const { silent = true } = opts ?? {}
    const res = await fetch(`${baseUrl}${url}`, baseOptions)

    if (!res.ok) throw await notifyOnError(res, silent, 'An unknown error occurred')

    return (await res.json()) as T
  }

  const download = async <T>(opts?: RequestOptions): Promise<T> => {
    const { silent = true } = opts ?? {}
    const baseOptionsL = {
      headers: {
        Authorization: `Bearer ${authStore.access_token}`,
      },
    }
    const res = await fetch(`${baseUrl}${url}`, baseOptionsL)

    if (!res.ok) throw await notifyOnError(res, silent, 'Failed to download file')

    return res as T
  }

  const buildHeaders = (isFormData: boolean): HeadersInit => {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${authStore.access_token}`,
    }
    if (!isFormData) {
      headers['Content-Type'] = 'application/json'
    }
    return headers
  }

  function toPayload(data?: unknown): BodyInit | undefined {
    if (!data) return undefined
    if (data instanceof FormData) return data
    return JSON.stringify(data)
  }

  const post = async <T>(body?: unknown, opts?: RequestOptions): Promise<T> => {
    const { silent = true } = opts ?? {}
    const isFormData = body instanceof FormData
    const headers = buildHeaders(isFormData)
    const payload = toPayload(body)

    const res = await fetch(`${baseUrl}${url}`, {
      ...baseOptions,
      method: 'POST',
      headers,
      body: payload,
    })

    if (!res.ok) throw await notifyOnError(res, silent, 'An unknown error occurred')

    if (res.status === 204 || res.headers.get('content-length') === '0') {
      return undefined as T
    }

    const text = await res.text()
    return text ? (JSON.parse(text) as T) : (undefined as T)
  }

  const del = async <T>(body?: unknown, opts?: RequestOptions): Promise<T> => {
    const { silent = true } = opts ?? {}
    const isFormData = body instanceof FormData
    const headers = buildHeaders(isFormData)
    const payload = toPayload(body)

    const res = await fetch(`${baseUrl}${url}`, {
      ...baseOptions,
      method: 'DELETE',
      headers,
      body: payload,
    })

    if (!res.ok) throw await notifyOnError(res, silent, 'An unknown error occurred')

    return res.status === 204 ? undefined : ((await res.json()) as T)
  }

  return { get, post, download, delete: del }
}
